import React, { useEffect, useState } from 'react';
import { CSVLink } from "react-csv";
import Pagination from '../../../Components/Pagination';
import { FiCalendar, FiDownload, FiSearch } from 'react-icons/fi';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import InputBox from '../../../Components/Input';
import { BiSolidDownArrow, BiSolidUpArrow } from 'react-icons/bi';
import CreateNewNeed from '../create-need';
import { useForm } from 'react-hook-form';
import createAxiosInstance from "../../../utils/axiosInstance";
import { API_ENDPOINTS } from '../../../utils/api';
import { formatDateShort, formatToUSD } from '../../../utils/formatters';
import usePageFilter from '../../../hooks/usePageFilter';
import { Link } from 'react-router-dom';
import Loader from '../../../Components/Loader';

const DonationsHome = () => {
  const axiosInstance = createAxiosInstance();

  const [recentDonationList, setRecentDonationList] = useState([]);
  const [cacheResult, setCacheResult] = useState<any[]>([]);
  const [rowPerPage, setRowPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoding, setIsLoding] = useState(true);
  const [defaultValues, setDefaultValues] = useState({
    search: "",
    startDate: null,
    endDate: null
  });
  // Sorting State
  const [sortColumn, setSortColumn] = useState<any>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const {
    register,
    watch,
    setValue,
    getValues
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues,
  });

  const watchSearch = watch("search");
  const watchStartDate = watch("startDate");
  const watchEndDate = watch("endDate");

  useEffect(() => {
    filterResult();
  }, [watchSearch, watchStartDate, watchEndDate]);

  const filterResult = () => {
    let result: any = cacheResult;
    if (getValues("search") !== "") {
      result = result.filter(
        (item: any) =>
          item.id.toString().includes(getValues("search")) ||
          formatDateShort(item.date).includes(getValues("search")) ||
          item.name.toLowerCase().includes(getValues("search").toLowerCase()) ||
          item.email.toLowerCase().includes(getValues("search").toLowerCase()) ||
          formatToUSD(item.cost)?.toString().includes(getValues("search")) ||
          formatToUSD(item.fee)?.toString().includes(getValues("search")) ||
          formatToUSD(item.amount)?.toString().includes(getValues("search"))
      );
    }
    const startDate = getValues("startDate");
    const endDate = getValues("endDate");
    if (startDate !== null && endDate !== null) {
      result = result.filter((item: any) => {
        const itemDate = new Date(formatDateShort(item.date));
        return itemDate >= new Date(startDate) && itemDate <= new Date(endDate);
      });
    }
    setTotalPages(Math.ceil(result.length / rowPerPage));
    handleSort(sortColumn, result, sortOrder);
  };

  // Handle Sorting
  const handleSort = (column: keyof typeof recentDonationList[0], result: any = recentDonationList, passed_order?: "asc" | "desc") => {
    let order: any = passed_order || sortColumn === column && sortOrder === "asc" ? "desc" : "asc";
    if (passed_order) {
      order = passed_order;
    }
    setSortColumn(column);
    setSortOrder(order);

    // Sort Data
    const sortedData: any = [...result].sort((a, b) => {
      if (a[column] < b[column]) return order === "asc" ? -1 : 1;
      if (a[column] > b[column]) return order === "asc" ? 1 : -1;
      return 0;
    });

    setRecentDonationList(sortedData);
  };

  const headers = [
    { label: "Donation #", key: "id" },
    { label: "Donation date", key: "date" },
    { label: "Name", key: "name" },
    { label: "Email", key: "email" },
    { label: "Cost of room", key: "cost" },
    { label: "Fee", key: "fee" },
    { label: "Donation amount", key: "amount" },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    register("startDate");
    register("endDate");
    getDonationList();
  }, []);

  const getDonationList = async () => {
    try {
      const response = await axiosInstance.get(`${API_ENDPOINTS.SAFENIIGHT.GETRECENTDONATIONS}?expand=need/assessments,donor&orderBy=createdAt+desc`);

      const data: any = [];

      response.data.map((element: any) => {
        const obj = {
          id: element.sequenceId,
          date: element.createdAt,
          name: element.donor ? `${element.donor.firstName} ${element.donor.lastName}` : `${element.firstName} ${element.lastName}`,
          email: element.donor?.email ?? element.email,
          cost: element?.need?.assessments?.[0]?.amount - element?.need?.assessments?.[0]?.fee,
          fee: element?.need?.assessments?.[0]?.fee,
          amount: element.amountReceived,
          referenceUrl: {
            id: `/safenight/donations/donation-detail/${element.donationId}`,
            name: element.donorId && element.donor ? `/safenight/donors/donor-detail/${element.donorId}` : null,
            email: element.email ? `mailto:${element.email}` : null,
          }
        };
        data.push(obj);
      });

      setTotalPages(Math.ceil(data.length / rowPerPage));
      handleSort("id", data, "desc");
      setCacheResult(data);
      setIsLoding(false);
    } catch (error) {
      setIsLoding(false);
      console.log(error);
    }
  };

  const updateStartDate = (date: any) => {
    setDefaultValues((prev) => ({ ...prev, startDate: date }));
    setValue("startDate", date);
  };

  const updateEndDate = (date: any) => {
    setDefaultValues((prev) => ({ ...prev, endDate: date }));
    setValue("endDate", date);
  };

  const filteredRequests = usePageFilter(
    recentDonationList,
    (currentPage - 1) * rowPerPage,
    rowPerPage
  );

  return (
    <>
      {isLoding && <Loader />}
      <div className="container py-4 md:py-10 2xl:py-[70px]">
        <div className="mb-3 md:mb-4 2xl:mb-8 flex justify-center">
          <CreateNewNeed />
        </div>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-gray-900 font-semibold text-xl">Donations</h3>
          {(recentDonationList && recentDonationList.length > 0) && (
            <CSVLink filename={`donations.csv`} data={recentDonationList} headers={headers}>
              <button className='btn btn-outline-gray py-2 2xl:py-3'>
                <FiDownload size={20} />
                Export to Excel
              </button>
            </CSVLink>
          )}
        </div>
        <div className="my-3 flex flex-wrap -mx-3">
          <div className="w-full sm:w-1/2 lg:w-1/4 px-3 flex items-center gap-3 justify-center mb-3 lg:mb-0">
            <label htmlFor="whatFees" className='block text-md font-medium text-gray-500 text-end'> From :</label>
            <div className="flex-1 relative">
              <DatePicker selected={defaultValues.startDate} onChange={(date: Date | null) => updateStartDate(date)} calendarClassName="ssc-custom-calendar" className='form-control w-full' />
              <FiCalendar size={20} className='text-gray-600 absolute top-1/2 -translate-y-1/2 right-3' />
            </div>
          </div>
          <div className="w-full sm:w-1/2 lg:w-1/4 px-3 flex items-center gap-3 justify-center mb-3 lg:mb-0">
            <label htmlFor="whatFees" className='block text-md font-medium text-gray-500 text-end'> To :</label>
            <div className="flex-1 relative">
              <DatePicker selected={defaultValues.endDate} onChange={(date: Date | null) => updateEndDate(date)} calendarClassName="ssc-custom-calendar" className='form-control w-full' />
              <FiCalendar size={20} className='text-gray-600 absolute top-1/2 -translate-y-1/2 right-3' />
            </div>
          </div>
          <div className="w-full sm:w-1/2 lg:w-1/4 px-3 flex items-center gap-3 justify-center mb-3 lg:mb-0">
            <label htmlFor="whatFees" className='block text-md font-medium text-gray-500 text-end'> Search :</label>
            <div className="flex-1">
              <InputBox
                type="search"
                placeholder="Search"
                iconRight={<FiSearch />}
                {...register('search')}
              />
            </div>
          </div>
        </div>
        <div className="relative md:overflow-hidden md:border md:border-borderGray rounded-xl">
          <table className="table_responsive_block w-full text-xs 2xl:text-sm text-left rtl:text-right">
            <thead className="text-xs 2xl:text-sm text-gray-600 bg-gray-100 border-b">
              <tr>
                {headers.map((col, index) => (
                  <th
                    key={index}
                    onClick={() => handleSort(col.key as keyof typeof recentDonationList[0])}
                    className="px-4 2xl:px-6 py-3 font-medium cursor-pointer"
                  >
                    {col.label}
                    {sortColumn === col.key && (sortOrder === 'desc' ? <BiSolidDownArrow className="inline text-xs ml-1" /> : <BiSolidUpArrow className="inline text-xs ml-1" />)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((donor: any, index: any) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td data-title='Donation #' className="px-4 2xl:px-6 py-3 font-medium text-primary cursor-pointer whitespace-nowrap"><Link to={donor.referenceUrl.id}> {donor.id} </Link></td>
                  <td data-title='Donation Date' className="px-4 2xl:px-6 py-3 text-gray-800">{formatDateShort(donor.date)}</td>
                  {donor.referenceUrl.name !== null ? <td data-title='Name' className="px-4 2xl:px-6 py-3 font-medium text-blue-300 cursor-pointer whitespace-nowrap"><Link to={donor.referenceUrl.name}>{donor.name}</Link></td>
                  : <td data-title='Name' className="px-4 2xl:px-6 py-3 font-medium whitespace-nowrap">{donor?.name}</td>}
                  <td data-title='Email' className="px-4 2xl:px-6 py-3 text-gray-800 font-medium"><Link to={donor.referenceUrl.email}><span className='truncate'>{donor.email}</span></Link></td>
                  <td data-title='Cost of room' className="px-4 2xl:px-6 py-3 text-gray-800">{formatToUSD(donor.cost)}</td>
                  <td data-title='Fee' className="px-4 2xl:px-6 py-3 text-gray-800">{formatToUSD(donor.fee)}</td>
                  <td data-title='Donation amount' className="px-4 2xl:px-6 py-3 text-gray-800">{formatToUSD(donor.amount)}</td>
                </tr>
              ))}
              {filteredRequests.length === 0 && (
                <tr>
                  <td colSpan={headers.length} className="text-center py-4 text-gray-500">There are no donations.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-5 flex justify-between items-center">
          <div></div>
          <Pagination totalPages={totalPages} onPageChange={handlePageChange} />
        </div>
      </div>
    </>
  );
};

export default DonationsHome;
